import { useEffect, useId, useRef, useState } from "react";
import { API_URL, api, formatApiErrorDetail } from "@/lib/api";
import { SMS_CONSENT_TEXT } from "@/lib/consent";

const MAX_FILES = 5;
const MAX_BYTES = 15 * 1024 * 1024;

/**
 * Coastal Energy service request form (ZIP contract): bottom of every service page,
 * id="request-service". Uploads PDF/JPEG/PNG/WebP, idempotent submission, real reference.
 */
export default function ServiceRequestForm({ content }) {
  const id = useId();
  const errorRef = useRef(null);
  const successRef = useRef(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", contactMethod: "email",
    street: "", city: "", state: "FL", zip: "",
    audience: "residential", authority: "owner", timing: "", message: "",
    configuration: "", answers: {}, marketingConsent: false, contactConsent: false,
    smsConsent: false, company_website: "",
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState(null);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (reference) successRef.current?.focus(); }, [reference]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setAnswer = (label, value) => setForm((f) => ({ ...f, answers: { ...f.answers, [label]: value } }));

  const phoneRequired = form.contactMethod === "phone" || form.contactMethod === "text";
  const phoneValid = !phoneRequired || (form.phone.replace(/\D/g, "").length >= 10 && form.phone.replace(/\D/g, "").length <= 15);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const step1Valid = form.name.trim().length >= 2 && emailValid && phoneValid && form.street.trim().length >= 6 && form.city.trim().length >= 2 && form.state.trim().length >= 2 && /^\d{5}(-\d{4})?$/.test(form.zip.trim()) && form.message.trim().length >= 10 && form.contactConsent;

  function fileError(list) {
    if (list.length > MAX_FILES) return `Upload up to ${MAX_FILES} files.`;
    if (list.some((f) => f.size > MAX_BYTES)) return "Each file must be 15 MB or smaller.";
    if (list.some((f) => !/\.(pdf|jpe?g|png|webp)$/i.test(f.name))) return "PDF, JPG, PNG or WebP files only.";
    return null;
  }

  function addFiles(incoming) {
    const next = [...files];
    for (const f of incoming) {
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    const err = fileError(next);
    if (err) { setError(err); return; }
    setFiles(next);
    setError("");
  }

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    if (!step1Valid) {
      const missing = [];
      if (form.name.trim().length < 2) missing.push("your name");
      if (!emailValid) missing.push("a valid email");
      if (!phoneValid) missing.push("a valid phone number (required for phone/text response)");
      if (form.street.trim().length < 6) missing.push("the property street address");
      if (form.city.trim().length < 2) missing.push("the city");
      if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) missing.push("a 5-digit ZIP");
      if (form.message.trim().length < 10) missing.push("a short message (what do you need?)");
      if (!form.contactConsent) missing.push("the contact consent checkbox");
      setError(missing.length ? `Almost there — please add: ${missing.join(", ")}.` : "Please review the highlighted fields.");
      errorRef.current?.focus();
      return;
    }
    setBusy(true);
    setError("");
    const idem = `sr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      const body = new FormData();
      body.append("payload", JSON.stringify({
        serviceId: content.id, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        contactMethod: form.contactMethod,
        address: `${form.street.trim()}, ${form.city.trim()}, ${form.state.trim()} ${form.zip.trim()}`,
        audience: form.audience, authority: form.authority, timing: form.timing,
        message: form.message.trim(), configuration: form.configuration,
        answers: form.answers, contactConsent: form.contactConsent,
        marketingConsent: form.marketingConsent, company_website: form.company_website,
        smsConsent: { granted: form.smsConsent, text: SMS_CONSENT_TEXT, recordedAt: new Date().toISOString() },
        pagePath: window.location.pathname,
      }));
      files.forEach((f) => body.append("files", f, f.name));
      const res = await fetch(`${API_URL}/service-requests`, {
        method: "POST", body, credentials: "include", headers: { "Idempotency-Key": idem },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.reference) throw new Error(formatApiErrorDetail(data.detail) || "Submission failed. Please retry or call 310-881-5155.");
      setReference(data.reference);
      successRef.current?.focus();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submission failed. Please retry or call 310-881-5155.");
    } finally {
      setBusy(false);
    }
  }

  const field = (label, control, testid, hint) => (
    <label className="sbb-field">
      <span>{label}</span>
      {control}
      {hint && <small style={{ fontSize: 11, color: "var(--muted)" }}>{hint}</small>}
      {testid && null}
    </label>
  );

  if (reference) {
    return (
      <section className="sbb-success" id="request-service" data-testid="srf-success">
        <h3 tabIndex={-1} ref={successRef}>Request received — {reference}</h3>
        <p>Your request for <b>{content.name}</b> is saved and in the review queue. The team follows up by {form.contactMethod === "text" ? "text" : form.contactMethod === "phone" ? "phone" : "email"} with next steps. Keep this reference — it identifies your request.</p>
        <p>A request is not a booked appointment or purchase. Duplicate submissions with the same request are combined automatically.</p>
      </section>
    );
  }

  return (
    <section id="request-service" style={{ marginTop: 34 }} data-testid="request-service-form">
      <span className="eyebrow">REQUEST</span>
      <h2>Request {content.name}</h2>
      <p className="muted" style={{ fontSize: 13, maxWidth: 640 }}>
        Tell us what you need. We'll follow up to discuss your property and next steps. A request is not a booked appointment
        or purchase.
      </p>
      <form onSubmit={submit} noValidate data-testid="service-request-form" style={{ marginTop: 18 }}>
        <input type="text" name="company_website" value={form.company_website} onChange={(e) => set("company_website", e.target.value)} style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div className="sbb-form-grid">
            {field("Name", <input required maxLength={160} autoComplete="name" data-testid="srf-name" value={form.name} onChange={(e) => set("name", e.target.value)} />)}
            {field("Email", <input required type="email" maxLength={254} autoComplete="email" data-testid="srf-email" value={form.email} onChange={(e) => set("email", e.target.value)} />)}
            {field(`Phone${phoneRequired ? "" : " (optional)"}`, <input type="tel" maxLength={30} autoComplete="tel" inputMode="tel" data-testid="srf-phone" value={form.phone} aria-invalid={form.phone.length > 0 && !phoneValid} onChange={(e) => set("phone", e.target.value)} />)}
            {field("Preferred response method", <select data-testid="srf-contact-method" value={form.contactMethod} onChange={(e) => set("contactMethod", e.target.value)}>
              <option value="email">Email</option><option value="phone">Phone</option><option value="text">Text</option>
            </select>)}
          </div>
          <div className="sbb-form-grid">
            {field("Property street address", <input required minLength={6} maxLength={200} autoComplete="street-address" data-testid="srf-street" value={form.street} onChange={(e) => set("street", e.target.value)} />)}
            {field("City", <input required maxLength={100} autoComplete="address-level2" data-testid="srf-city" value={form.city} onChange={(e) => set("city", e.target.value)} />)}
            {field("State", <input required maxLength={20} autoComplete="address-level1" data-testid="srf-state" value={form.state} onChange={(e) => set("state", e.target.value)} />)}
            {field("ZIP", <input required maxLength={10} inputMode="numeric" autoComplete="postal-code" data-testid="srf-zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} />)}
          </div>
          <div className="sbb-form-grid">
            {field("Residential or commercial", <select data-testid="srf-audience" value={form.audience} onChange={(e) => set("audience", e.target.value)}>
              <option value="residential">Residential</option><option value="commercial">Commercial</option>
            </select>)}
            {field("You are the…", <select data-testid="srf-authority" value={form.authority} onChange={(e) => set("authority", e.target.value)}>
              <option value="owner">Property owner</option><option value="tenant">Tenant</option><option value="authorized">Authorized representative</option>
            </select>)}
            {field("Timing", <select data-testid="srf-timing" value={form.timing} onChange={(e) => set("timing", e.target.value)}>
              <option value="">Not sure yet</option><option value="ASAP">As soon as possible</option><option value="1-3 months">1–3 months</option><option value="3-6 months">3–6 months</option><option value="planning">Planning ahead</option>
            </select>)}
            {content.pricingRows?.length > 1 && field("Which configuration interests you?", <select data-testid="srf-configuration" value={form.configuration} onChange={(e) => set("configuration", e.target.value)}>
              <option value="">Not sure — help me choose</option>
              {content.pricingRows.filter((r) => r.status !== "historical").map((r) => <option key={r.item} value={`${r.item} (${r.amount})`}>{r.item} — {r.amount}</option>)}
            </select>)}
          </div>
          {content.questions?.length > 0 && (
            <div className="sbb-form-grid">
              {content.questions.map((q, i) => (
                <label className="sbb-field" key={q.label} data-testid={`srf-question-${i + 1}`}>
                  <span>{q.label}</span>
                  {q.type === "select" ? (
                    <select value={form.answers[q.label] || ""} onChange={(e) => setAnswer(q.label, e.target.value)}>
                      <option value="">Not sure</option>
                      {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input maxLength={300} value={form.answers[q.label] || ""} placeholder={q.hint || ""} onChange={(e) => setAnswer(q.label, e.target.value)} />
                  )}
                </label>
              ))}
            </div>
          )}
          {field("Message — what do you need?", <textarea required minLength={10} maxLength={2000} rows={3} data-testid="srf-message" value={form.message} onChange={(e) => set("message", e.target.value)} />)}
          <div className="sbb-drop" role="button" tabIndex={0} aria-label="Attach bills, photos, plans or screenshots (optional)"
            onClick={() => document.getElementById(`${id}-files`)?.click()}
            onKeyDown={(e) => { if (e.key === "Enter") document.getElementById(`${id}-files`)?.click(); }}>
            <strong>Attach bills, photos, plans or screenshots (optional)</strong>
            <span>Up to {MAX_FILES} files · PDF, JPG, PNG or WebP · 15 MB each</span>
            <input id={`${id}-files`} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple style={{ display: "none" }} onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
          </div>
          {files.length > 0 && (
            <ul className="sbb-files" data-testid="srf-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <span>{f.name} · {(f.size / 1048576).toFixed(1)} MB</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} aria-label={`Remove ${f.name}`}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <label className="sbb-check" style={{ marginTop: 14 }}>
            <input required type="checkbox" checked={form.contactConsent} data-testid="srf-consent" onChange={(e) => set("contactConsent", e.target.checked)} />
            I agree to be contacted about this service request using my selected contact method and have read the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </label>
          <label className="sbb-check">
            <input type="checkbox" checked={form.smsConsent} data-testid="srf-sms-consent" onChange={(e) => set("smsConsent", e.target.checked)} />
            {SMS_CONSENT_TEXT}
          </label>
          <label className="sbb-check">
            <input type="checkbox" checked={form.marketingConsent} data-testid="srf-marketing" onChange={(e) => set("marketingConsent", e.target.checked)} />
            Optionally, send me occasional solar tips and offers. (Marketing messages require a separate opt-in.)
          </label>
        </fieldset>
        {error && <p className="sbb-error" role="alert" tabIndex={-1} ref={errorRef} data-testid="srf-error" style={{ marginTop: 12 }}>{error}</p>}
        <div className="sbb-modal-actions" style={{ marginTop: 14 }}>
          <a href="tel:+13108815155">310-881-5155</a>
          <button className="sbb-primary" type="submit" disabled={busy} data-testid="srf-submit">{busy ? "Sending…" : "Request My Service Estimate"}</button>
        </div>
        <p className="sbb-hint">A request is not a booked appointment or purchase. All requests go to the project team's own inbox and are stored with a reference you can keep.</p>
      </form>
    </section>
  );
}
